<template>
  <div class="page-company" v-if="isAdmin">
    <PageHeader>
      <div class="heading">
        <h1>
          Admin / <strong class="name">{{ company.name }}</strong>
        </h1>
      </div>

      <div class="header-actions">
        <Permissions
          :permissions="currentUser.permissions"
          :requireAll="{site: ['create']}"
        >
          <Btn tertiary tooltip="Meet ProBot!" @click="addSite">
            Create a new website
          </Btn>
        </Permissions>
      </div>
    </PageHeader>

    <div class="page-columns">
      <div class="col main">
        <div class="page-content">
          <div v-if="hasSites">
            <Permissions
              :permissions="currentUser.permissions"
              :requireAll="{site: ['list']}"
            >
              <section v-if="hasSites">
                <table-data
                  :rows="sites"
                  :headings="[
                    {key: 'edit', heading: 'Website Name'},
                    {key: 'domainSettings', heading: 'Domain Name'},
                    {key: 'activeStatus', heading: 'Active Status'}
                  ]"
                >
                  <div
                    v-for="(site, index) in sites"
                    :slot="'td-' + index + '-edit'"
                    :key="index"
                  >
                    <router-link
                      :to="{
                        name: 'site',
                        params: {companyId: companyId, siteId: site.id}
                      }"
                    >
                      {{ site.name }}
                    </router-link>
                  </div>

                  <div
                    v-for="(site, index) in sites"
                    :slot="'td-' + index + '-domainSettings'"
                    :key="index"
                  >
                    <router-link
                      :to="{
                        name: 'site',
                        params: {companyId: companyId, siteId: site.id}
                      }"
                    >
                      {{ site.domain }}
                    </router-link>
                  </div>

                  <div
                    v-for="(site, index) in sites"
                    :slot="'td-' + index + '-activeStatus'"
                    :key="index"
                  >
                    <router-link
                      :to="{
                        name: 'plan',
                        params: {companyId: companyId, siteId: site.id}
                      }"
                    >
                      <div
                        class="active-state"
                        :class="{'is-active': site.subscription_active}"
                      >
                      </div>
                    </router-link>
                  </div>
                </table-data>

                <pagination @changePage="changePage" :pagination="pageData">
                </pagination>
              </section>

              <section>

              </section>
            </Permissions>
          </div>

          <div class="website-intro" v-else>
            <p>This user doesn't have any websites to show.</p>
          </div>
        </div>
      </div>

      <div class="col side">
        <PageSummary>
          <div>
            <h1>Admin Links</h1>
            <div class="btn-wrapper btn-admin-links is-block">
              <router-link
                class="btn is-subtle"
                :to="{name: 'admin-website',
                params: {
                  companyId: companyId
                }}">
                Create Website
              </router-link>
            </div>

            <div class="btn-wrapper btn-admin-links is-block">
              <router-link
                class="btn is-subtle"
                :to="{name: 'edit-company',
                params: {
                  companyId: companyId,
                  userId: currentUser.id
                }}">
                Edit Company
              </router-link>
            </div>

            <div class="btn-wrapper btn-admin-links is-block">
              <router-link
                class="btn is-subtle"
                :to="{name: 'company-domains'}">
                Company Domains
              </router-link>
            </div>

            <div class="btn-wrapper btn-admin-links is-block">
              <router-link
                class="btn is-subtle"
                :to="{name: 'company-users'}">
                Company Team
              </router-link>
            </div>

            <div class="btn-wrapper btn-admin-links is-block">
              <router-link
                class="btn is-subtle"
                :to="{name: 'billing'}">
                Company Billing
              </router-link>
            </div>
          </div>
        </PageSummary>
      </div>
    </div>

    <router-view :companyName="company.name" />
  </div>
</template>

<script>
import page from '@/mixins/page'
import pagination from '@/mixins/pagination'

import {mapState} from 'vuex'

import {readCompanyData} from '@/lib/companies'
import {readCompanySitesData} from '@/lib/sites'

import Table from '@/components/Table'
import Btn from '@/components/Btn'
import PageHeader from '@/components/PageHeader'
import PageSummary from '@/components/PageSummary'
import Permissions from '@/components/Permissions'

export default {
  name: 'company',

  mixins: [page, pagination],

  props: {
    companyId: {
      type: String,
      required: true
    }
  },

  components: {
    TableData: Table,
    Btn,
    PageHeader,
    PageSummary,
    Permissions
  },

  data() {
    return {
      company: {},
      sites: []
    }
  },

  methods: {
    getData() {
      readCompanyData(this.companyId).then(({data}) => {
        this.company = data
      })

      readCompanySitesData(this.companyId, this.pagination).then(
        ({data, page}) => {
          this.sites = data
          this.pageData = page
        }
      )
    },

    addSite() {
      this.$router.push({name: 'getStarted'})
    }
  },

  computed: {
    ...mapState(['currentUser']),

    title() {
      return `${this.company.name} Websites`
    },

    isAdmin() {
      return this.currentUser.admin
    },

    hasSites() {
      return !!this.sites.length
    }
  },

  mounted() {
    this.getData()
  },

  watch: {
    $route() {
      this.getData()
    }
  }
}
</script>

<style lang="sass">
@import '../stylesheets/base/variables'
@import '../stylesheets/helpers/mixins'
@import '../stylesheets/helpers/extends'

.page-company
  @extend %page

  .btn-admin-links
    margin: $gap-small 0

  .website-intro
    text-align: center
    margin: $gap-medium auto
    padding-bottom: $gap-medium
    max-width: 500px

    .sprite
      fill: rgba(0, 0, 0, 0.05)
      width: 160px
      height: 160px
      margin-bottom: $gap-medium
</style>
